import { XIcon } from "@phosphor-icons/react/dist/csr/X";
import { Button } from "@volca/ui/components/ui/button";
import { SimpleSidebar } from "@volca/ui/components/ui/simple-sidebar";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { GuideChecklistView } from "./guide-checklist";
import { useGuide } from "./guide-context";
import { GuideMarkdown } from "./guide-markdown";
import { AP_ADDRESS_TOKEN } from "./guides";
import { useOnboardingState } from "./onboarding-state-context";

/**
 * The open guide, beside the step rather than over it. A customer reads a guide while
 * doing the step, so covering the step is exactly wrong: they would be closing and
 * reopening it at every instruction.
 */
export const GuidePanel = () => {
  const guide = useGuide()?.open ?? null;
  const setOpen = useGuide()?.setOpen;
  const address = useOnboardingState()?.ap_inbound_email;

  // Written once with a token, so the guide reads as the customer's own setup rather
  // than as a template. Without an address yet, the token would read as a bug.
  const body = guide
    ? guide.body.replaceAll(
        AP_ADDRESS_TOKEN,
        address ?? "your Volca address, once we have set it up",
      )
    : "";

  return (
    <SimpleSidebar aria-label="Setup guide">
      {guide && (
        <>
          <Stack
            direction="row"
            justify="between"
            align="start"
            gap={2}
            className="w-full border-b border-line px-5 py-4"
          >
            <Stack align="start" gap={0} className="min-w-0">
              <Text
                weight="semibold"
                variant="caption"
                tone="muted"
                className="tracking-wider uppercase"
              >
                Volca setup guide
              </Text>
              <Text variant="title">{guide.title}</Text>
            </Stack>
            <Button
              variant="ghost"
              size="icon-xs"
              aria-label="Close guide"
              onClick={() => setOpen?.(null)}
            >
              <XIcon className="h-4 w-4" />
            </Button>
          </Stack>

          {/* Keyed on the guide, so opening another one starts at its top rather
              than wherever the last one was left. */}
          <div key={guide.id} className="min-h-0 flex-1 overflow-y-auto">
            <Stack gap={2} align="stretch" className="w-full px-5 py-4">
              <Text tone="muted">{guide.summary}</Text>
              <GuideMarkdown>{body}</GuideMarkdown>
              {guide.checklist && (
                <GuideChecklistView checklist={guide.checklist} />
              )}
            </Stack>
          </div>
        </>
      )}
    </SimpleSidebar>
  );
};
